import { Glass } from "@/constants/glass";
import { BorderRadius, Colors } from "@/constants/theme";
import type { Theme } from "@/constants/theme";
import { Platform } from "react-native";

/**
 * The Floor's design tokens — 1:1 from "Fahali Floor v3.dc.html".
 * The Floor renders with its own quiet-luxury palette (cream/gold-ink)
 * keyed off the app's light/dark mode. Semantic risk colors are shared
 * across both themes, exactly as in the design.
 */
export interface FloorPalette {
  bg: string;      // screen ground
  sf: string;      // card surface
  sf2: string;     // soft gold surface (emphasis)
  seg: string;     // segmented-control / track ground
  ln: string;      // hairline
  ink: string;     // primary text
  mut: string;     // secondary text
  fnt: string;     // faint text / labels
  goldT: string;   // gold accent (text-safe)
  green: string;
  amber: string;
  red: string;
  exit: string;
  slate: string;
  /**
   * SECTION accent — the colour that makes a phone screen navigable.
   *
   * Section labels ("CHECK THE WORK", "HOW IT BUILT"), the evidence timeline's
   * spine and its nodes all used `fnt`/`ln` — #6E6A62 and #E4E1D6 on a #FAF8F2
   * page. Measured, `ln` is a 1.1:1 hairline: on glass at arm's length the rails
   * and the group boundaries simply are not there, so every section read as one
   * undifferentiated column of text.
   *
   * Text-safe by construction, unlike Yield Gold (see the note below): 7.0:1 on
   * Warm Sand, 6.6:1 on Obsidian. Safe for small uppercase labels, which is
   * exactly where it is used.
   */
  accent: string;
  /** Tinted GROUND for grouped blocks — replaces near-invisible hairlines. */
  accentWash: string;
}

/**
 * 🎨 THE FOUNDER'S TEXT HIERARCHY — stated 2026-08-15, applied literally.
 *
 * These five hexes are the founder's, verbatim, and they are not to be
 * "corrected" to darkened siblings again. Measured against the ground the text
 * ACTUALLY lands on — the FloorCard surface, `rgba(255,255,255,0.88)` over
 * `#ECE6DB`, which resolves to **#FEFEFC** — not against the page:
 *
 *     ink   Obsidian       #171714  17.04:1  PASS
 *     mut   Slate          #504B43   7.80:1  PASS
 *     fnt   Silver         #6B6459   4.05:1  AA-LARGE ONLY (see below)
 *     red   Critical Red   #943A32   4.47:1  0.03 under AA-normal
 *     slate Contagion Blue #5C6C82   5.30:1  PASS
 *
 * 🔴 MEASURE AGAINST THE CONTAINER, NOT THE PAGE. An earlier pass rejected
 * three of these as AA failures using the PAGE ground (#ECE6DB), where they
 * read 15.70 / 7.18 / 3.73 / 4.12 / 4.88. Almost no Floor text sits on the
 * page — 319 of 320 ThemedText nodes are inside a FloorCard. Measuring the
 * wrong ground rejected colours that were fine and is the same defect class as
 * `#A97826` being 2.34:1 on cream and 6.71:1 on Obsidian.
 *
 * ⚠️ `fnt` at 4.05:1 is a DELIBERATE, founder-owned exception to WCAG-AA at
 * small size (it clears the 3:1 large-text bar). It carries meta labels and
 * timestamps only — never prose. Prose in this tier was the "fine print"
 * defect fixed on 2026-08-15; `floorInkRoles.test.ts` holds that line.
 */
// ⚠️ `goldT` is the TEXT-safe gold and must never hold Yield Gold (#A97826).
//
// The founder's Warning Gold now lives in `amber`, which is the FILL slot their
// own spec puts it in ("Status & Highlights"). It cannot also be the text gold:
// #A97826 measures 2.54:1 on the card, 2.31:1 on sand, 2.34:1 on the page — it
// clears nothing, not even the 3:1 large-text bar — and this token paints 43
// text bindings across 17 Floor files (section labels, "View →" affordances).
//
// #8A5A12 is that same Warning Gold darkened 35% along its own hue, which is the
// closest value to the founder's that clears AA on ALL THREE grounds the token
// actually lands on: 5.44 card / 4.94 sand / 5.01 page. The previous #8A5A12
// also passed but read browner — further from the intent for no extra safety.
//
// A palette sweep put the fill value here once and it took a contrast audit to
// notice, because gold-on-cream looks *fine* until it is measured.
const LIGHT: FloorPalette = {
  bg: Colors.light.backgroundRoot,
  sf: Colors.light.surfaceCard,
  sf2: Colors.light.surfaceDeep,
  seg: Colors.light.backgroundElevated,
  ln: Colors.light.ruleBold,
  ink: Colors.light.textPrimary,
  mut: Colors.light.textSecondary,
  fnt: Colors.light.textTertiary,
  goldT: Colors.light.goldText,
  green: Colors.light.riskGreen,
  amber: Colors.light.primary,
  red: Colors.light.riskRed,
  exit: "#7A211D",
  slate: Colors.light.steelBlue,
  accent: Colors.light.steelBlue,
  accentWash: Colors.light.steelSoft,
};

const DARK: FloorPalette = {
  bg: Colors.dark.backgroundRoot,
  sf: Colors.dark.surfaceCard,
  sf2: Colors.dark.surfaceDeep,
  seg: Colors.dark.backgroundElevated,
  ln: Colors.dark.ruleBold,
  ink: Colors.dark.textPrimary,
  mut: Colors.dark.textSecondary,
  fnt: Colors.dark.textTertiary,
  goldT: Colors.dark.goldText,
  green: Colors.dark.riskGreen,
  amber: Colors.dark.primary,
  red: Colors.dark.riskRed,
  exit: "#FF9B91",
  slate: Colors.dark.steelBlue,
  accent: Colors.dark.steelBlue,
  accentWash: Colors.dark.steelSoft,
};

export function floorPalette(isDark: boolean): FloorPalette {
  return isDark ? DARK : LIGHT;
}

/* ── SECTION HUES ──────────────────────────────────────────────────────────────
 *
 * A hue per step of the Floor's argument, carried as GROUND rather than accent.
 *
 * The Floor is a long scroll of eight cards, every one of them `#FFFFFF` on a
 * `#F1EFE7` page — two near-identical creams — with every section header in the
 * same grey and the same gold rail. Nothing on the page told a reader where in
 * the sequence they were, so it scrolled as one undifferentiated column.
 *
 * Previous attempts at this added ACCENTS: a 2px rail, a 20px icon mark, a
 * tinted footnote. Measured against the actual complaint they failed for a
 * structural reason — at arm's length on a phone, thin marks do not read as
 * colour at all. Filled AREA does. So the hue lands on the section's ground and
 * the cards keep their frosted white, which is also the only arrangement in
 * which the glass reads as glass: frost needs something behind it to be frosted
 * over.
 *
 * What the colour MEANS is position in the sequence — wayfinding, which is real
 * information about the page. It deliberately does not encode sentiment: a
 * section does not turn red because the news is bad. Risk hues stay on the
 * figures that measured something (`red`/`green`/`amber` above), so a coloured
 * ground can never be misread as a verdict.
 *
 * ⚠️ `label` is the only member safe for TYPE. `rail` and `ground` are fill-only.
 * This is the same discipline that `goldT` exists to enforce (see the note above
 * it): Yield Gold is 2.3:1 on Warm Sand and looks perfectly fine until measured.
 * Each `label` here is a ~30%-lightness sibling of its rail, checked against its
 * own ground rather than against the page — e.g. #26497F on #E8EFF9 is 7.8:1.
 * If you add a hue, measure it on its ground before using it for the label.
 */
export interface SectionHue {
  /** Fill-only. The header rail. */
  rail: string;
  /** Fill-only. The section's ground, behind header and cards alike. */
  ground: string;
  /** Text-safe on `ground` — AA at caption size. */
  label: string;
}

export type SectionHueName = "signal" | "growth" | "insight" | "record" | "warning" | "structure";

const HUES_LIGHT: Record<SectionHueName, SectionHue> = {
  signal:    { rail: "#5C6C82", ground: "#ECE6DB", label: "#504B43" },
  growth:    { rail: "#176448", ground: "#ECE6DB", label: "#176448" },
  insight:   { rail: "#6B6459", ground: "#ECE6DB", label: "#504B43" },
  record:    { rail: "#A97826", ground: "#ECE6DB", label: "#8A5A12" },
  warning:   { rail: "#943A32", ground: "#ECE6DB", label: "#943A32" },
  structure: { rail: "#504B43", ground: "#ECE6DB", label: "#504B43" },
};

const HUES_DARK: Record<SectionHueName, SectionHue> = {
  signal:    { rail: "#7FA8E8", ground: "#1B2333", label: "#9EBEF0" },
  growth:    { rail: "#6FA88C", ground: "#182825", label: "#8ECCBF" },
  insight:   { rail: "#A87BC4", ground: "#241C2C", label: "#C4A3D6" },
  record:    { rail: "#D9AE5F", ground: "#2A2318", label: "#E0BC79" },
  warning:   { rail: "#EC8C80", ground: "#2C1E1C", label: "#EC9A90" },
  structure: { rail: "#8296AE", ground: "#1D2229", label: "#A8B6C6" },
};

export function sectionHue(name: SectionHueName, isDark: boolean): SectionHue {
  return (isDark ? HUES_DARK : HUES_LIGHT)[name];
}

/**
 * The Floor's card surface, as frosted glass.
 *
 * The Floor's eight feature cards each render their own container with
 * `{ backgroundColor: p.sf, borderColor: p.ln }` — an opaque fill and a single
 * flat border — rather than going through the shared card primitives. That is
 * why the design system's glass did not reach the app's most-viewed screen
 * even after every primitive was converted: nothing on the Floor used them.
 *
 * This routes those containers to the SAME formula as GlassSurface instead of
 * copying it, so the two cannot drift.
 *
 * Takes `isDark` only — it resolves the theme itself, because the call sites
 * destructure `{ isDark }` from useTheme and mostly do not hold `theme`.
 */
export function floorGlass(isDark: boolean, float = false) {
  const theme = Colors[isDark ? "dark" : "light"] as Theme;
  return [
    {
      /**
       * Opaque floor under the frost. Without it the ambient blobs show through
       * at full strength and body text loses contrast.
       *
       * Raised from 0.62 -> 0.88 (light) and 0.55 -> 0.82 (dark). A card at
       * rgba(252,251,248,0.62) over a #FAF8F2 page resolved to roughly a 2%
       * step: on a phone the cards did not read as cards, so the whole screen
       * was one flat sheet and the eye had no edges to rest on. The page ground
       * also darkened slightly, so the step is made from both sides.
       */
      backgroundColor: isDark ? "rgba(35,33,32,0.82)" : "rgba(255,255,255,0.88)",
      borderRadius: BorderRadius.outer,
    },
    Glass.surface(theme, isDark, float),
    {
      /**
       * Lift. Glassmorphism is frost PLUS elevation — without a shadow the
       * blur reads as a slightly different flat colour rather than a pane
       * floating above the page. Soft and wide (radius 18, y+6) so it reads as
       * depth rather than a drop shadow, and warm-black rather than neutral so
       * it sits inside the Park Avenue palette.
       */
      shadowColor: isDark ? "#000000" : "#121210",
      shadowOpacity: isDark ? 0.38 : 0.12,
      shadowRadius: float ? 30 : 22,
      shadowOffset: { width: 0, height: float ? 14 : 8 },
      elevation: float ? 9 : 5,
    },
  ];
}

/**
 * THE HEADER IS ALWAYS THE DARK PLANE — it does not follow light/dark mode.
 *
 * The design brief specifies `Header Glass (Dark) rgba(36,34,31,0.9)` as a
 * fixed value, and `Glass.darkSurface()` has implemented it exactly since the
 * Park Avenue pass. Nothing called it: FloorHeader painted `p.sf`/`p.seg` onto
 * a bare View carrying only a bottom border, so on the cream canvas the top of
 * the screen had no plane at all — it read as page, not as chrome, while the
 * tab bar at the bottom of the same screen was obsidian.
 *
 * 🔴 WHY THIS IS ITS OWN PALETTE AND NOT `floorPalette(true)`.
 *
 * Reusing the DARK floor palette is the obvious move and it ships an
 * accessibility regression. Measured against the header fill:
 *
 *     ink   #FAF8F2  14.27:1  PASS
 *     mut   #D2CDC3   8.67:1  PASS
 *     fnt   #AAA397   4.39:1  *** FAILS AA (needs 4.5) ***
 *
 * `fnt` carries the identity line ("Browsing the live floor · portfolio not
 * connected") and the clock — the two pieces of header text a stranger reads
 * first. #AAA397 lifts it to 5.32:1. This is the same defect the LIGHT palette
 * above already carries a note about (#73716A at 4.456:1, across 207 call
 * sites, caught only by a contrast audit): a faint token on a new ground looks
 * fine and is not.
 *
 * Ratios are computed against #24221F — the brief's stated value. The real
 * `glassDarkFill` is `rgba(18,18,16,0.92)`, which is DARKER, so every number
 * here is conservative.
 */
export const HEADER_INK = {
  ink: "#FAF8F2",
  mut: "#D2CDC3",
  fnt: "#AAA397",
  goldT: "#D9AE5F",
  green: "#6FA88C",
  amber: "#D9AE5F", //  7.68:1
  red: "#EC8C80",   //  4.76:1
  slate: "#A8B6C6",
  /** Hairlines and control fills ON the dark plane — alpha, so the frost shows. */
  ln: "rgba(255,255,255,0.14)",
  sf: "rgba(255,255,255,0.10)",
  seg: "rgba(0,0,0,0.26)",
} as const;

/**
 * The feed-status pill — the brief's `Terminal Green (Live) #176448` fill with
 * `Terminal Text (Live) #CBE2D4` on it (4.61:1, measured).
 *
 * It replaces a bare 8px dot. A coloured dot with no label is a guess, and the
 * founder's rule is that a user who has to think has already been failed — so
 * the pill says which of the three states it is. That also keeps it HONEST:
 * `feedStatus` is live | stale | offline, and a green-only indicator could only
 * ever have encoded the flattering one.
 *
 * Stale and offline get their own dark grounds rather than a tint of the status
 * colour: a tint at this alpha over the header plane lands ~1.4–1.8:1 against
 * the chrome, so the chip stops reading as a chip exactly when it matters most.
 */
export const FEED_PILL = {
  live:    { bg: "#176448", fg: "#FAF8F2", label: "LIVE" },
  stale:   { bg: "#4A3C1E", fg: "#F0DCAE", label: "STALE" },    // 7.95:1
  offline: { bg: "#4A2320", fg: "#F5CBC4", label: "OFFLINE" },  // 9.16:1
} as const;

/**
 * The brief's bold metrics, Floor-local.
 *
 * ⚠️ Deliberately NOT written into `BorderRadius.card` / `Spacing`. Those are
 * app-wide: moving the shared card radius from 18 to 28 restyles every screen
 * in the product, and the ask was the Home tab.
 *
 * `metaTracking` is the one that buys the most "designed" per pixel. The brief
 * calls wide letter-spacing "crucial for the terminal feel" at 0.2em–0.25em;
 * the Floor was at 0.8. NOTE RN's unit is POINTS, not em — 0.2em at an 11px
 * label is 2.2, so the old value was roughly a third of the intent.
 */
export const FloorMetrics = {
  /** Brief: outer cards `rounded-[28px]`. */
  cardRadius: 28,
  /** Brief: inner elements `rounded-2xl`. */
  innerRadius: 16,
  /** Brief: `p-8`. Whitespace is the luxury signal; the Floor was at 18. */
  cardPadding: 26,
  /** Brief: meta labels at 0.2em–0.25em, uppercase. Points, not em. */
  metaTracking: 2.2,
} as const;

/** color-mix(color X%, transparent) equivalent for RN: hex alpha suffix. */
export function tint(hex: string, pct: number): string {
  const a = Math.round((pct / 100) * 255).toString(16).padStart(2, "0");
  return `${hex}${a}`;
}

export const MONO = "JetBrainsMono_500Medium, ui-monospace, 'SF Mono', 'Cascadia Code', Consolas, monospace";
/**
 * The text face. Inter leads.
 *
 * These were pure SYSTEM stacks — so on web the body copy rendered in Segoe UI
 * or San Francisco while Inter sat self-hosted, declared and serving 200 from
 * /fonts/Inter-Variable.woff2, asked for by nothing. Two of the three brand
 * faces were unreachable at the same time (Fraunces and JetBrains Mono were
 * declared `src: local("Georgia")` / `local("Consolas")`, i.e. never
 * downloaded), so the Floor's entire typographic identity was absent on web
 * and the screen read as a generic system-font page.
 *
 * The system stack stays as the fallback, so nothing regresses if Inter fails.
 */
export const SANS = Platform.OS === "web"
  ? "var(--font-inter), Inter, system-ui, sans-serif"
  : "Inter_400Regular";
export const SANS_SEMI = Platform.OS === "web"
  ? "var(--font-inter), Inter, system-ui, sans-serif"
  : "Inter_600SemiBold";
// The editorial voice. Fraunces is the display face the design brief is drawn
// against and is bundled with the app (expo-font registers it at runtime);
// Iowan Old Style leads the fallbacks for devices where the face never loads.
/** Product headings. Kept as a compatibility name for existing Floor code. */
export const DISPLAY = SANS_SEMI;
/** The single human verdict or market meaning on a screen. */
export const EDITORIAL =
  "'Fraunces_400Regular', 'Iowan Old Style', 'Apple Garamond', Baskerville, 'Times New Roman', 'Droid Serif', Times, 'Source Serif Pro', serif";

/** Tier ladder used by locked previews (public pricing, LOCKED 2026-06-16). */
export const TIER_LEVEL: Record<string, number> = {
  retail: 0, professional: 1, elite: 2, desk: 3, institutional: 3, enterprise: 4,
};

/**
 * 🔓 FEATURE GATES ARE OPEN — founder decision, 2026-08-01.
 *
 * Mirrors TIER_GATES_ENABLED in server/middleware.ts; both must agree or the app
 * shows a card the API then refuses, which is worse than either state alone.
 *
 * Use `tierAllows()` instead of comparing TIER_LEVEL by hand. Every hand-rolled
 * comparison read `TIER_LEVEL[tier ?? "retail"]`, which defaults an UNKNOWN tier
 * to the lowest one — so while billing was loading, or on any expired token, an
 * enterprise admin was shown "Elite · $299/mo" and an upgrade button on their own
 * Floor. That is what this replaces.
 */
export const TIER_GATES_ENABLED = false;

/**
 * Does this tier reach `min`? Open while gates are off.
 *
 * When gates return, note the second argument: an UNKNOWN tier is not a low tier.
 * Callers should treat `undefined` as "not yet known" and render a loading state,
 * never a lock.
 */
export function tierAllows(tier: string | undefined, min: keyof typeof TIER_LEVEL): boolean {
  if (!TIER_GATES_ENABLED) return true;
  if (!tier) return false;
  return (TIER_LEVEL[tier] ?? 0) >= (TIER_LEVEL[min] ?? 0);
}
export const TIER_PRICE: Record<string, number> = {
  retail: 19, professional: 79, elite: 299, desk: 1499, institutional: 1499, enterprise: 4999,
};
export function priceGap(fromTier: string | undefined, toTier: "elite" | "desk"): string {
  const from = TIER_PRICE[fromTier ?? "retail"] ?? 19;
  const to = TIER_PRICE[toTier];
  if ((TIER_LEVEL[fromTier ?? "retail"] ?? 0) >= TIER_LEVEL[toTier]) return "included";
  return `+$${(to - from).toLocaleString()}/mo`;
}
